<template>
  <div v-if="showModal">
    <div id="overlayAll" @click="hideModal"></div>
    <div
      role="alertdialog"
      aria-haspopup="dialog"
      aria-modal="true"
      aria-live="assertive"
      aria-describedby="modal-description"
      class="modal-container"
      :class="modal.options.size && 'size-' + modal.options.size"
      @click.self="hideModal"
      @keydown.esc="hideModal"
    >
      <div class="modal-content" ref="modalContent">
        <div class="modal-close" v-if="modal.options.showClose">
          <button
            ref="closeButton"
            type="button"
            :aria-label="$t('modal.closeButton')"
            @click="hideModal"
            @keydown.esc.prevent="hideModal"
            @keydown.tab.prevent="handleTab"
          ></button>
        </div>
        <div class="modal-text">
          <component v-if="modalComponent" v-bind:is="modalComponent" :aria-labelledby="modalName" />
          <div v-else>
            <div id="modal-title" v-if="isModalTitleSet">{{ modal.options.title }}</div>
            <div id="modal-description">{{ modal.text }}</div>
          </div>
        </div>
        <div class="modal-button" v-if="!modal.options.hideButton">
          <button
            ref="modalButton"
            class="btn"
            @click="hideModal"
            @keydown.esc.prevent="hideModal"
            @keydown.tab.prevent="handleTab"
          >
            {{ $t('modal.confirmButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onUpdated, ref, watch } from 'vue'
import { useStore } from 'vuex'

const VisaHelpModal1 = defineAsyncComponent(
  () => import('@/base/components/Payment/VisaMobileProcess/Modals/VisaHelpModal1')
)
const VisaHelpModal2 = defineAsyncComponent(
  () => import('@/base/components/Payment/VisaMobileProcess/Modals/VisaHelpModal2')
)
const AliorModal = defineAsyncComponent(() => import('@/base/components/Payment/Paywall/Modals/AliorModal'))
const PaykaModal = defineAsyncComponent(() => import('@/base/components/Payment/Paywall/Modals/PaykaModal'))
const Click2PayModal = defineAsyncComponent(() => import('@/base/components/Payment/Paywall/Modals/Click2PayModal'))
const Click2PayPrcessErrorModal = defineAsyncComponent(
  () => import('@/base/components/Payment/Paywall/Modals/Click2PayPrcessErrorModal')
)

defineOptions({ name: 'Modals' })

const store = useStore()

const showModal = ref(false)
// Flaga ubija odroczony `applyBodyScrollLock` — bez niej zaplanowana klatka mogłaby przywrócić
// `overflow: hidden` już po odmontowaniu i zablokować przewijanie strony na stałe.
let unmounted = false
const modalContent = ref(null)
const closeButton = ref(null)
const modalButton = ref(null)

const modal = computed(() => store.getters.getModal)
const returnFocusRefId = computed(() => store.state.messages.focusReturnElementId)
// `<component :is>` dostaje w szablonie STRING ze store'a (`modal.component`), a `<script setup>`
// nie rejestruje komponentów pod nazwami tak jak opcja `components` - trzeba rozwiązać nazwę na
// definicję samemu, inaczej modal renderuje się jako nieznany element i znika bez śladu.
const MODAL_COMPONENTS = {
  VisaHelpModal1,
  VisaHelpModal2,
  AliorModal,
  PaykaModal,
  Click2PayModal,
  Click2PayPrcessErrorModal,
}

const modalName = computed(() => modal.value.component)
const modalComponent = computed(() => MODAL_COMPONENTS[modalName.value] ?? null)
const isModalTitleSet = computed(() => String(modal.value.options.title).length > 0)

function setInert(hideAll) {
  const elements = document.querySelectorAll('main, footer, .header .container')
  elements.forEach((element) => {
    if (hideAll) {
      element.setAttribute('inert', '')
    } else {
      element.removeAttribute('inert')
    }
  })
}

function hideModal() {
  // `getModal` zwraca `null`, gdy nie ma już modala do pokazania, a zamknięcie potrafi się
  // wywołać dwa razy pod rząd: overlay ma `@click.self` i `@keydown.esc`, przycisk zamknięcia
  // i przycisk potwierdzenia - obydwa również. Drugie wywołanie trafiało na pusty getter
  // (`showModal` zdejmuje szablon dopiero watcherem, czyli po przerysowaniu) i wysypywało się
  // na `null.id`. Modal jest wtedy już zamknięty - nie ma czego raportować.
  if (modal.value) {
    store.dispatch('removeMessage', modal.value.id)
  }
  setInert(false)
  const returnElement = document.getElementById(returnFocusRefId.value)
  if (returnElement) {
    returnElement.focus()
  }
}

function handleTab() {
  if (!modal.value.options.hideButton) {
    modalButton.value.focus()
    return
  }
  if (modal.value.options.showClose) {
    closeButton.value.focus()
  }
}

// Blokada przewijania idzie klatkę po powstaniu `.modal-container` (`requestAnimationFrame`,
// nie `nextTick` — `nextTick` to wciąż ta sama klatka). Zdjęcie przewijalności dokumentu w tej
// samej klatce, w której powstaje warstwa `position: fixed`, zmienia w webview wysokość
// viewportu; odroczenie zostaje jako zabezpieczenie, ale deformacji z SD-41216 nie powodowało —
// tam winna była wymuszona pełna wysokość kontenera (`_modals.scss`).
// → `doc/dokumentacja/architektura.md`
async function applyBodyScrollLock() {
  await nextTick()
  await new Promise((resolve) => requestAnimationFrame(resolve))
  if (unmounted) {
    return
  }
  document.body.style.overflow = showModal.value ? 'hidden' : ''
}

watch(modal, (val) => {
  showModal.value = typeof val === 'object' && val !== null
  void applyBodyScrollLock()
})

onUpdated(() => {
  if (showModal.value) {
    if (!modal.value.options.hideButton) {
      modalButton.value.focus()
    } else {
      if (modal.value.options.showClose) {
        closeButton.value.focus()
      }
    }
    setInert(true)
  }
})

onBeforeUnmount(() => {
  unmounted = true
  document.body.style.overflow = ''
  setInert(false)
})
</script>
